import {
  Backdrop,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  FormControlLabel,
  FormHelperText,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import * as Yup from 'yup';
import { DatePicker } from '@mui/lab';
import { useSnackbar } from 'notistack';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { useMemo, useState } from 'react';
import getFileData from 'src/utils/getFileData';
import { getFhirIdFromEntity } from 'src/utils/fhir';
import { UploadFiles } from 'src/careflow/common';
import { yupResolver } from '@hookform/resolvers/yup';
import { every, isEmpty, isEqual, isNil } from 'lodash';
import { useOrganizations } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { InsuredOptionProps } from 'src/@types/crs/referral';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { Controller, SubmitHandler, useForm, useFormContext } from 'react-hook-form';
import {
  DocumentReference, 
} from 'src/careflow/ph-core/fhir-client/resources';
import { getBackEndBaseUrl } from 'src/utils/domain-utils';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';

const PARTS = [
  { value: 'A', label: 'Part A' },
  { value: 'B', label: 'Part B' },
  { value: 'C', label: 'Part C' },
  { value: 'D', label: 'Part D' },
];

interface Parts {
  partChecked: string | undefined;
  partDate?: Date;
}

type FormValues = {
  insuranceId: string;
  insuranceCompany: string;
  parts: Parts[];
  files: Blob[];
  documents: string[];
};

type Props = InsuredOptionProps;

export default function Medicare({
  patient,
  documentReference,
  coverage,
  serviceRequest,
  handleCreateBynary,
  handleCoverage,
  handleCreateDocumentReference,
  handleUpdateDocumentReference,
  handleUpdateResource,
  onCancel,
}: Props) {
  
  const { enqueueSnackbar } = useSnackbar();

  const authUser = useAuth().getCurrentUser();
  const { configurations } = useTenantConfigData();

  const [openBackdrop, setOpenBackdrop] = useState(false);

  const [ organization, ] = useOrganizations();
  const [errorParts, setErrorParts] = useState('');

  const EventSchema = Yup.object().shape({
    insuranceId: Yup.string().required('Medicare Number is required'),
    parts: Yup.array(
      Yup.object({
        partChecked: Yup.string()
      })
    ).test(
      'part_checked_test',
      'At least one partChecked must be checked',
      (value) => {
        const checked = !every(value, ['partChecked', undefined]);
        if(!checked){
          setErrorParts('At least one partChecked must be checked');
        }
        return checked;
      } 
    )
  });

  let partEdit: Parts[] = [];

  const edit = Boolean(coverage?.id);

  if(coverage?.class?.[0].name?.split(' ')[2] === 'A'){
    partEdit = [{
      partChecked:coverage?.class?.[0].name?.split(' ')[2] || '',
      partDate: coverage?.period?.start && new Date(coverage.period.start) || undefined
    }]
  }else if (coverage?.class?.[0].name?.split(' ')[2] === 'B'){
    partEdit = [{partChecked: undefined},{
      partChecked:coverage?.class?.[0].name?.split(' ')[2] || '',
      partDate: coverage?.period?.start && new Date(coverage.period.start) || undefined
    }]
  }else if(coverage?.class?.[0].name?.split(' ')[2] === 'C'){
    partEdit = [{partChecked: undefined},{partChecked: undefined},{
      partChecked:coverage?.class?.[0].name?.split(' ')[2] || '',
      partDate: coverage?.period?.start && new Date(coverage.period.start) || undefined
    }]
  }else if(coverage?.class?.[0].name?.split(' ')[2] === 'D'){
    partEdit = [{partChecked: undefined},{partChecked: undefined},{partChecked: undefined},{
      partChecked:coverage?.class?.[0].name?.split(' ')[2] || '',
      partDate: coverage?.period?.start && new Date(coverage.period.start) || undefined
    }]
  };

  const documentsRelated = useMemo(
    () => {
      let docs;
      if(serviceRequest && coverage){
        docs = documentReference?.
          filter(d => d.context?.related?.
            find( r => r.reference === `${serviceRequest?.resourceType}/${serviceRequest?.id}`
              && r.reference === `${coverage?.resourceType}/${coverage?.id}`)).
                map((e) => e.id)

      }else if(coverage){
        docs = documentReference?.
        filter(d => d.context?.related?.
          find( r => r.reference === `${coverage?.resourceType}/${coverage?.id}`)).
            map((e) => e.id)
      }else if(serviceRequest){
        docs = documentReference?.
        filter(d => d.context?.related?.
          find( r => r.reference === `${serviceRequest?.resourceType}/${serviceRequest?.id}`)).
            map((e) => e.id)
      }
      return docs;
    },
    [documentReference, serviceRequest, coverage]
  );

  const defaultValues = {
    insuranceId: coverage?.subscriberId || '',
    parts: partEdit,
    documents: documentsRelated || [],
  };

  const methods = useForm<FormValues>({
    resolver: yupResolver(EventSchema),
    defaultValues,
  });

  const { watch, handleSubmit } = methods;

  const values = watch();

  const { parts } = values;

  const convertBlobToBase64 = (file: Blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        resolve(reader.result?.toString().split(',')[1]);
      };
      reader.readAsDataURL(file);
  });

  const createBinaryCustom = async (data: FormValues): Promise<any> => {
    if (!data?.files?.length) return null;

    const resourceType = 'Binary';

    const mappedFile =  await Promise.all( 
      data.files.map(async (file: Blob) => {
        const contentType = file.type;
        const data = await convertBlobToBase64(file);
        const securityContext = { reference: `${patient?.resourceType}/${patient?.id}` };
        const name = getFileData(Object.assign(file)).name;
        const obj = { resourceType, contentType, securityContext, data};
        const binary = await handleCreateBynary(obj);

        const objNew = {...binary[0], name: name};
        return objNew;
      })
    );

    return mappedFile;
  };
  
  const createDocumentReference = async (binaryList: any) => {

    const organizationFhir = organization?.
    filter(e => e.id === authUser.organization_fhir_uri).
    map((r) => ({
      display: r.name, 
      reference: `Organization/${r.id}`
    }))[0];

    const documentReference = binaryList?.map((binary:any) => ({ 
      resourceType: 'DocumentReference',
      status: 'current',
      subject: { reference: `${patient?.resourceType}/${patient?.id}` },
      author: [{ 
        ...(!isEmpty(authUser.user_fhir_uri.trim()) && {
          reference: `Practitioner/${getFhirIdFromEntity(authUser.user_fhir_uri)}` 
        }), 
        display: authUser.name
      }],
      ...(organizationFhir && {
        custodian: organizationFhir
      }),
      date: new Date().toISOString(),
      content: [{ attachment: { 
        contentType: `${binary.contentType}`, 
        creation: new Date().toISOString(),
        title: binary.name, 
        url: `${configurations?.fhir?.baseUrl}/Binary/${binary.id}` 
      }}],
     }));

     const createdDocumentReference = await handleCreateDocumentReference(documentReference);
     
    return createdDocumentReference;
  };
  
  const mapCoverage = (data: FormValues) => {
    
    const { insuranceId, parts } = data;

    const mapCoverage = parts.map((opt) => {

      if(opt.partChecked !== undefined){

        const id = coverage?.id;
        const resourceType = 'Coverage';
        const status = 'active';
        const subscriberId = insuranceId;
        const beneficiary = { reference: `${patient?.resourceType}/${patient?.id}` };
        const payor = [{ display: 'medicare_Centers for Medicare & Medicaid Services (CMS)' }];
        const clas = [
          {
            type: {
              coding: [
                {
                  system: 'http://terminology.hl7.org/CodeSystem/coverage-class',
                  code: 'plan',
                },
              ],
            },
            value: 'medicare',
            name: `Medicare Part ${opt.partChecked}`,
          },
        ];
        const period = { start: opt.partDate };
  
        return { resourceType, id, status, subscriberId, beneficiary, payor, class: clas, period };
      }
      
    }).filter(e => e !== undefined);

    return mapCoverage;
  };
  
  const updateDocumentsSelected = async (
    data: FormValues, 
    createdDocs: DocumentReference[] | undefined,
    resourceToRelate: any
  ): Promise<any> => {

    const setServiceRequest = serviceRequest !== undefined ? 
      [`${serviceRequest?.resourceType}/${serviceRequest?.id}`] : [];

    const setCoverage = !isNil(coverage) ? 
      `${coverage?.resourceType}/${coverage?.id}` :
       Array.isArray(resourceToRelate) ? 
        `${resourceToRelate?.[0]?.resourceType}/${resourceToRelate?.[0]?.id}` :
        `${resourceToRelate?.resourceType}/${resourceToRelate?.id}`;

    const setCoverageArray = setCoverage ? [setCoverage] : [];

    const { documents } = data;

    const delRelatedResource = documentsRelated?.filter((e:any) => !documents?.some((s:any) => s === e));

    const documentsNotRelated = documentReference?.filter(p => delRelatedResource?.
      some((f:any) => p.id === f)).
        map((l) =>{

          const related = l.context?.related || [];  

          const setReferences = [...setServiceRequest, ...setCoverageArray];
          const notRelated = related?.
            filter(r => !setReferences?.some((x1) => x1 === r.reference)) || [];
          
          if(l.context?.related && !isEmpty(notRelated)){ 
            l.context.related = [ ...notRelated ];
          }else{
            delete l.context?.related;
          }

          if(isEmpty(l.context)){
            delete l.context;
          }

          const {...result} = l;
          return result;
    })|| [];

    const documentsSelected = documents?.filter((e:any) => !documentsRelated?.some((s:any) => s == e));

    const documentReferenceSelected = documentReference?.filter(d => documentsSelected?.
      some((r: any) => d.id === r)).
        map((e) => {
          const context = e.context;
          const related = e.context?.related || [];  

          let resourceRelated: any;
          resourceRelated = related.
            filter(r => setServiceRequest?.some((x1) => x1 === r.reference));

          if(setCoverageArray?.length > 0){
            resourceRelated = resourceRelated?.
              filter((j: any) => setCoverageArray?.some((x1) => x1 === j.reference))
          }
            
          if(isEmpty(resourceRelated)){
            const setReferences = [...setServiceRequest, ...setCoverageArray];
            const mapReference: any = setReferences?.map((x2) => ({reference: x2 })); 
            related?.push(...mapReference);
          }

          if(isEmpty(resourceRelated)){
            e.context = { 
              ...context, 
              related: [ 
                ...related
            ]};
          }else {
            e.context = {...context, related: [ ...related ]};
          }

          const {...result} = e;
          return result;
    }) || [];

    const createdDocuments = createdDocs?.map((e) => {
      const context = e.context;
      const related = e.context?.related || [];

      let resourceRelated: any;
          resourceRelated = related.
            filter(r => setServiceRequest?.some((x1) => x1 === r.reference));

          if(setCoverageArray?.length > 0){
            resourceRelated = resourceRelated?.
              filter((j: any) => setCoverageArray?.some((x1) => x1 === j.reference))
          }
            
      if(isEmpty(resourceRelated)){
        const setReferences = [...setServiceRequest, ...setCoverageArray];
        const mapReference: any = setReferences?.map((x2) => ({reference: x2 })); 
        e.context = {...context, related: [ ...related, ...mapReference]};
      }else {
        e.context = {...context, related: [ ...related ]};
      }
      const {...result} = e;
      return result;
    }) || [];

    const allDocuments = [ ...documentsNotRelated, ...documentReferenceSelected, ...createdDocuments];

    const updateDocumentReference = await handleUpdateDocumentReference(allDocuments);

    if(!isNil(serviceRequest)){
      const relateDocuments = [ ...documentReferenceSelected, ...createdDocuments];
      const getDocuments = mapDocumentReferenceToResource(resourceToRelate, relateDocuments, documentsNotRelated);

      if(getDocuments.length > 0 && serviceRequest){
        serviceRequest.supportingInfo = getDocuments;
      }else if(serviceRequest){
        delete serviceRequest.supportingInfo;
      }

      handleUpdateResource(serviceRequest);
    }

    return allDocuments;
  };

  const updateDocuments = ( data: FormValues ) => {
    const { documents, files } = data;

    const arraysDocsEqual = isEqual(documents, documentsRelated);

    if((documentsRelated && 
      (documents.length !== documentsRelated.length)) || 
      !arraysDocsEqual ||
      files?.length > 0
    ){
      return true;
    }
    return false;
  };

  const mapDocumentReferenceToResource = (
    resource: any, 
    documentReference: DocumentReference[], 
    notRelate: DocumentReference[]
  ) => {
    const document = documentReference?.map((doc) => (`${doc?.resourceType}/${doc?.id}`));
    const notRelateMap = notRelate?.map((nDoc) => (`${nDoc?.resourceType}/${nDoc?.id}`));
    const supportingInfo = resource?.supportingInfo?.map((sd) => (`${sd?.reference}`)) ?? [];
    const setDocument = [...new Set([...supportingInfo, ...document])];
    const allDocuments = setDocument?.
      filter((x1) => !notRelateMap.
        some((x2) => x1 === x2)).map((x3) => ({reference: x3 })); 
    return allDocuments;
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      setOpenBackdrop(true);

      let documentReferences;
      let resourceToRelated: any;

      if(data.files !== undefined){
        const createBinary = await createBinaryCustom(data);
        documentReferences = await createDocumentReference(createBinary);
      }

      const mapCoverageResult = mapCoverage(data);
      if((Array.isArray(mapCoverageResult) && mapCoverageResult?.length > 0) || !isEmpty(mapCoverageResult)){
        resourceToRelated = await handleCoverage(mapCoverageResult);
      }
      const update = updateDocuments(data);

      if(update){
        const updateDocuments = await updateDocumentsSelected(data, documentReferences, resourceToRelated);
      }

      setOpenBackdrop(false);
      onCancel();
    } catch (e) {
      onCancel();
      enqueueSnackbar('An error has occurred.', { variant: 'error' });
    }
  };

  const handleParts = () => setErrorParts('');

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid container spacing={3} sx={{ p: 2 }}>
        <Grid item xs={6}>
          <Stack direction="column" alignItems="left" justifyContent="space-between">
            <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
              Medicare Number*
            </Typography>
            <RHFTextField name="insuranceId" />
          </Stack>
        </Grid>

        <MedicareParts 
          parts={parts} 
          edit={edit} 
          errorParts={errorParts} 
          handleParts={handleParts}
        />

      </Grid>
      <Typography variant="body2" sx={{ mt: 1, ml: 2 }}>
        Medicare Card Front and Back
      </Typography>
      <UploadFiles
        label='insurance'
        restrictFiles={true}
        documentReferences={documentReference}
        patient={patient}
        docRelated={documentsRelated}
      />
      <Stack alignItems="center">
        <DialogActions>
          <Button variant="outlined" color="info" type="submit">
            Save
          </Button>
        </DialogActions>
      </Stack>
    </FormProvider>
  );
}

type MedicarePartsProp = {
  parts: Parts[];
  edit: boolean;
  errorParts: string;
  handleParts: () =>  void;
};

function MedicareParts({ 
  parts, 
  edit, 
  errorParts,
  handleParts 
}: MedicarePartsProp) {
  
  const methods = useFormContext();

  const { control } = methods;

  return (
    <>
      <Grid item xs={6}>
        <Typography variant="body2" sx={{ color: 'text.secondary', pt: 1 }}>
          Medicare Parts*
        </Typography>

        <Stack
          direction={{ xs: 'row', md: 'row' }}
          alignItems="center"
          spacing={2}
          sx={{ width: 1, pt: 2, '& .MuiFormGroup-root': { flexDirection: 'row' } }}
        >
          {PARTS.map((e: any, index) => (
            <div key={e.value}>
              <FormControlLabel
                control={
                  <Controller
                    key={index}
                    name={`parts.${index}.partChecked`}
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value !== undefined}
                        disabled={edit}
                        value={e.value}
                        onChange={() => {
                          field.onChange(e.value !== field.value ? e.value : undefined);
                          handleParts();
                        }}
                      />
                    )}
                  />
                }
                disabled={edit}
                label={e.label}
              />
            </div>
          ))}
        </Stack>
        {errorParts && 
          <FormHelperText sx={{ color: '#FF4842'}}>
            {errorParts}
          </FormHelperText>
        }
      </Grid>

      <Grid item xs={12}>
        <Stack direction="row" alignItems="right">
          {parts?.map(
            (option: any, index: number) =>
              option.partChecked && (
                <Stack key={`stack${index}`} direction="column" sx={{ px: 1 }}>
                  <Typography
                    key={`typo${index}`}
                    variant="body2"
                    sx={{ color: 'text.secondary', py: 1 }}
                  >
                    Coverage {option.partChecked} Starts
                  </Typography>
                  <Controller
                    key={index}
                    name={`parts.${index}.partDate`}
                    control={control}
                    defaultValue={new Date()}
                    render={({ field, fieldState: { error } }) => (
                      <DatePicker
                        value={field.value}
                        onChange={(newValue) => {
                          field.onChange(newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            error={!!error}
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Stack>
              )
          )}
        </Stack>
      </Grid>
    </>
  );
}
